import type { Product } from '@/types'

import { useRef } from 'react'
import Link from 'next/link'

import { useCart } from '@/lib/cart'
import { gsap, registerGsap, wfEase, prefersReducedMotion } from '@/lib/gsap'
import { useIsomorphicLayoutEffect } from '@/hooks/use-isomorphic-layout-effect'

const money = (value: number) => `$${value.toFixed(2)} USD`

function ShopItem({ product }: { product: Product }) {
    const { addItem } = useCart()
    const ref = useRef<HTMLDivElement>(null)

    // a-74 "Fade In Slow": each product fades in as it scrolls into view.
    useIsomorphicLayoutEffect(() => {
        const el = ref.current
        if (!el) return
        registerGsap()

        if (prefersReducedMotion()) {
            gsap.set(el, { opacity: 1, y: 0 })
            return
        }

        const ctx = gsap.context(() => {
            gsap.fromTo(
                el,
                { opacity: 0, y: 30 },
                {
                    opacity: 1,
                    y: 0,
                    duration: 1.2,
                    ease: wfEase('ease'),
                    scrollTrigger: { trigger: el, start: 'top 88%', once: true }
                }
            )
        }, ref)

        return () => ctx.revert()
    }, [])

    return (
        <div
            ref={ref}
            role='listitem'
            className='shop-item w-dyn-item'
            style={{ opacity: 0 }}
        >
            <Link
                href={`/shop/${product.slug}`}
                className='product-item-link w-inline-block'
                data-cursor='big'
            >
                <div className='small-thumbnail-image'>
                    <img
                        src={product.image}
                        alt={product.name}
                        loading='lazy'
                        className='shop-item-image'
                    />
                </div>
                <div className='shop-item-content'>
                    <div className='shop-item-title-price'>
                        <div className='shop-item-title'>{product.name}</div>
                        <div className='shop-item-price'>{money(product.price)}</div>
                    </div>
                </div>
            </Link>
            <div className='product-add-to-card-holder'>
                <button
                    type='button'
                    className='add-to-cart-button'
                    onClick={() => addItem(product)}
                    style={{ cursor: 'none' }}
                >
                    Add to Cart
                </button>
            </div>
        </div>
    )
}

export function ShopGrid({ products }: { products: Product[] }) {
    return (
        <div className='shop-wrapper w-dyn-list'>
            <div role='list' className='shop-list w-dyn-items'>
                {products.map((product) => (
                    <ShopItem product={product} key={product.slug} />
                ))}
            </div>
        </div>
    )
}
